import { Component, Vue, Prop, Ref } from 'vue-property-decorator';
import CameraModal from '@/components/cameramodal/cameramodal.component.vue';
import CameraModalCreate from '@/components/cameramodalcreate/cameramodalcreate.component.vue';
import { Camera } from '@/models/camera';
import { Project } from '@/models/project';
import { User } from '@/models/user';
import ProjectModal from '@/components/projectmodal/projectmodal.component.vue';
import { State } from 'vuex-class';
import { Storage } from 'aws-amplify';
import AWS from 'aws-sdk';
import { Auth } from 'aws-amplify';

async function configureAWSCredentials() {
  try {
    const credentials = await Auth.currentCredentials();
    AWS.config.update({
      credentials: credentials,
      region: 'eu-west-3', // Remplacez par votre région AWS
    });
    console.log('AWS Credentials configurés avec succès.');
  } catch (error) {
    console.error('Erreur lors de la configuration des credentials AWS:', error);
  }
}

@Component({
  components: {
    ProjectModal,
    CameraModal,
    CameraModalCreate,
  },
})
export default class ProjectCard extends Vue {
  @Prop({ required: true }) readonly isPremium!: boolean;
  @Ref('videoPlayer') readonly videoPlayer!: HTMLVideoElement;

  public lastPhotos: {
    url: string;
    rotation: number;
    createdAt: Date;
    ratio?: string;
    zoom?: number;
    apiFetchInterval?: number;
    id: string;
  }[] = [];
  public currentPhotoIndex = 0;
  public nextPhotoIndex = (this.currentPhotoIndex + 1) % this.lastPhotos.length;
  public isTransitioning = false;
  public imageInterval: number | null = null;
  public camera: Camera | null = null;
  public selectedPhotoIndex = -1;
  public currentPhotoId = '';
  public overlayshow = false;
  public timelapseVideoUrl = '';
  public timelapseByCamera: Record<string, string> = {};
  public logoUrl: string | null = null;

  @Prop({ type: Object, required: true }) private readonly project!: Project;
  @State('user', { namespace: 'profile' }) private user!: User;

  public handleTouchStart(): void {
    this.overlayshow = true;
    this.$nextTick(() => {
      const video = this.videoPlayer;
      if (video && this.timelapseByCamera[this.lastPhotos[this.currentPhotoIndex].id]) {
        video.play(); // Démarre la vidéo au toucher
      }
    });
  }

  public handleTouchEnd(): void {
    this.overlayshow = false;
    const video = this.videoPlayer;
    const progressBar = this.$el.querySelector('.card-header .progress-bar') as HTMLElement;
    if (video) {
      video.pause(); // Arrête la vidéo quand le toucher est relâché
      video.currentTime = 0; // Remet au début
    }
    if (progressBar) {
      progressBar.classList.remove('active'); // Réinitialise l’animation
      progressBar.style.width = '0'; // Remet la barre à 0
    }
  }

  public async fetchLogoUrl() {
    try {
      if (!this.project || !this.project.owner) {
        console.warn('❌ `project` ou `project.owner` est indéfini. Attente...');
        return;
      }

      const region = 'eu-west-3';
      const credentials = await Auth.currentCredentials();
      const s3 = new AWS.S3({ region, credentials });

      const ownerID = this.project?.owner.id;
      const projectID = this.project.id;

      console.log('🔹 Récupération du logo pour ownerID:', ownerID, 'projectID:', projectID);

      const prefix = `public/${ownerID}/${projectID}/Logo/`;

      const response = await s3
        .listObjectsV2({
          Bucket: 'erige-main-store-prod',
          Prefix: prefix,
        })
        .promise();

      if (response.Contents && response.Contents.length > 0) {
        const lastFile = response.Contents.sort(
          (a, b) => (b.LastModified?.getTime() || 0) - (a.LastModified?.getTime() || 0)
        )[0];

        if (lastFile && lastFile.Key) {
          this.logoUrl = s3.getSignedUrl('getObject', {
            Bucket: 'erige-main-store-prod',
            Key: lastFile.Key,
            Expires: 3600,
          });

          console.log('✅ URL du logo récupérée :', this.logoUrl);
        }
      } else {
        console.warn('❌ Aucun logo trouvé dans le bucket.');
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du logo :', error);
    }
  }

  public onVideoPlay(event: Event): void {
    console.log('Vidéo en lecture => on stoppe le timer 4000ms');
    if (this.imageInterval !== null) {
      window.clearInterval(this.imageInterval);
      this.imageInterval = null;
    }

    const video = event.target as HTMLVideoElement;
    const progressBar = this.$el.querySelector('.card-header .progress-bar') as HTMLElement;
    if (video && progressBar) {
      video.addEventListener('loadedmetadata', () => {
        progressBar.style.animationDuration = `${video.duration}s`;
        progressBar.classList.add('active');
      });
      if (video.duration) {
        progressBar.style.animationDuration = `${video.duration}s`;
        progressBar.classList.add('active');
      }
    }
  }

  public onVideoEnded(): void {
    console.log('Vidéo terminée => on passe à la photo suivante et on relance le timer.');
    const progressBar = this.$el.querySelector('.card-header .progress-bar') as HTMLElement;
    if (progressBar) {
      progressBar.style.width = '100%'; // Remet à 0 pour le prochain survol
    }
    this.overlayshow = false;
    this.nextImage();
    this.startInterval();
  }

  public updateProgress(event: Event): void {
    const video = event.target as HTMLVideoElement;
    const progressBar = this.$el.querySelector('.card-header .progress-bar') as HTMLElement;
    if (video) {
      console.log(`Progression vidéo: ${video.currentTime}/${video.duration}`);
    }
  }

  public nextImage(): void {
    this.currentPhotoIndex = (this.currentPhotoIndex + 1) % this.lastPhotos.length;
    this.currentPhotoId = this.lastPhotos[this.currentPhotoIndex].id;
    this.nextPhotoIndex = (this.currentPhotoIndex + 1) % this.lastPhotos.length;
    this.camera =
      this.project.cameras.find((camera) => camera.id === this.lastPhotos[this.currentPhotoIndex].id) || null;

    if (this.imageInterval !== null) {
      window.clearInterval(this.imageInterval);
    }

    this.imageInterval = window.setInterval(() => {
      this.nextImage();
    }, 4000);
  }

  public prevImage(): void {
    this.currentPhotoIndex = (this.currentPhotoIndex - 1 + this.lastPhotos.length) % this.lastPhotos.length;
    this.currentPhotoId = this.lastPhotos[this.currentPhotoIndex].id;
    this.nextPhotoIndex = (this.currentPhotoIndex + 1) % this.lastPhotos.length;
    this.camera =
      this.project.cameras.find((camera) => camera.id === this.lastPhotos[this.currentPhotoIndex].id) || null;

    if (this.imageInterval !== null) {
      window.clearInterval(this.imageInterval);
    }

    // Reprendre le défilement avant
    this.imageInterval = window.setInterval(() => {
      this.nextImage();
    }, 4000);
  }

  public hasDetectionIa(): boolean {
    return this.project.cameras?.some((camera) => camera.detectionia) || false;
  }

  public hasLive(): boolean {
    return this.project.cameras?.some((camera) => camera.live) || false;
  }

  public isMoreThan48Hours(photo: { createdAt: Date; apiFetchInterval?: number }): boolean {
    const diffTime = Math.abs(new Date().getTime() - new Date(photo.createdAt).getTime());
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
    if (diffHours >= 48) {
      return photo.apiFetchInterval === 60; // Si le diffHours est supérieur ou égal à 48, alors vérifier que apiFetchInterval est 60
    }
    return false; // Si le diffHours est inférieur à 48, alors retourner false
  }

  public isDifferentFetchIntervalAndMoreThan48Hours(photo: { createdAt: Date; apiFetchInterval?: number }): boolean {
    const diffTime = Math.abs(new Date().getTime() - new Date(photo.createdAt).getTime());
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
    return diffHours >= 48 && photo.apiFetchInterval !== 60;
  }

  public handleClick(event: MouseEvent): void {
    const elementHeight = (event.target as Element).clientHeight;
    const clickPosition = event.clientY - (event.target as Element).getBoundingClientRect().top;

    if (clickPosition < elementHeight - 40) {
      const routeData = this.$router.resolve({
        name: 'plateformcentral',
        params: { id: this.project.id, cameraId: this.lastPhotos[this.currentPhotoIndex].id },
      });
      window.open(routeData.href, '_blank');
    }
    // Arrêtez l'intervalle lorsque la modale est ouverte
    if (this.imageInterval !== null) {
      window.clearInterval(this.imageInterval);
      this.imageInterval = null;
    }
  }

  public handleClickOnDate(): void {
    if (this.imageInterval !== null) {
      window.clearInterval(this.imageInterval);
      this.imageInterval = null;
    }

    const modal = this.$refs['camera-modal-' + this.currentPhotoId] as Vue & { show: () => void };
    modal.show();
  }

  public startInterval(): void {
    // Démarrer l'intervalle lorsque la modale est fermée
    if (this.imageInterval === null) {
      this.imageInterval = window.setInterval(() => {
        this.nextImage();
      }, 4000);
    }
  }

  public async mounted(): Promise<void> {
    // 1) Configure AWS (si besoin)
    await configureAWSCredentials();

    this.$el.addEventListener('mouseover', () => {
      const progressBar = this.$el.querySelector('.card-header .progress-bar') as HTMLElement;
      if (progressBar) {
        progressBar.classList.remove('active'); // Retire l’animation précédente
        progressBar.style.width = '0'; // Réinitialise
      }
    });

    // 2) On concatène les caméras actuelles + anciennes
    const allCameras = [...(this.project.cameras || []), ...(this.project.previousCameras || [])];

    if (allCameras.length > 0) {
      // 3) On crée un tableau de promesses : pour CHAQUE caméra, on charge photo + vidéo
      const promises = allCameras.map((cam) => {
        if (!cam.lastPhoto) {
          // Si pas de lastPhoto, on fait juste un Promise.resolve()
          return Promise.resolve();
        }

        // --- A) CHARGER L'IMAGE (smallKey -> medium) ---
        const modifiedKey = cam.lastPhoto.smallKey.replace('-small', '-medium');
        const photoPromise = Storage.get(modifiedKey).then((url: any) => {
          this.lastPhotos.push({
            url: url as string,
            id: cam.id,
            rotation: cam.rotate || 0,
            createdAt: cam.lastPhoto?.createdAt ?? new Date(),
            ratio: '',
            zoom: 1,
            apiFetchInterval: cam.apiFetchInterval || 0,
          });
        });

        // --- B) CHARGER LA VIDÉO TIMELAPSE ---
        const videoKey = `${this.user.id}/${this.project.id}/${cam.id}/timelapse.mp4`;
        const videoPromise = Storage.get(videoKey)
          .then((videoUrl: any) => {
            // On enregistre l’URL dans le dictionnaire
            this.timelapseByCamera[cam.id] = videoUrl as string;
          })
          .catch((err) => {
            console.warn(`Pas de timelapse pour la caméra ${cam.id}`, err);
            // On stocke au moins une chaîne vide pour ne pas re-demander plus tard
            this.timelapseByCamera[cam.id] = '';
          });

        // Retourne la promesse combinée
        return Promise.all([photoPromise, videoPromise]) as unknown as Promise<void>;
      });

      // 4) On attend la fin de TOUTES les promesses
      await Promise.all(promises);

      // Limiter le tableau lastPhotos (optionnel)
      this.lastPhotos = this.lastPhotos.slice(0, 15);

      // Calcul du ratio + zoom sur CHAQUE photo
      this.lastPhotos.forEach((photo) => {
        const img = new Image();
        img.onload = () => {
          photo.ratio = `${img.width}:${img.height}`;
          if (photo.ratio === '1920:691') {
            photo.zoom = 1.4;
          }
        };
        img.src = photo.url;
      });

      // Définir la caméra courante (optionnel)
      if (this.project.cameras.length > 0) {
        this.camera = this.project.cameras[0];
      }
    }

    if (this.project) {
      await this.fetchLogoUrl();
    }

    // 5) Initialiser le changement d'image automatique
    this.imageInterval = window.setInterval(() => {
      this.nextImage(); // ou ta logique de changement d'image
    }, 4000);
  }

  public beforeDestroy(): void {
    // Assurez-vous de nettoyer l'intervalle lorsque le composant est détruit
    if (this.imageInterval !== null) {
      window.clearInterval(this.imageInterval);
    }
  }

  public get currentPhoto(): string {
    return this.lastPhotos[this.currentPhotoIndex]?.url || '';
  }

  public get currentPhotoRotation(): number {
    return this.lastPhotos[this.currentPhotoIndex]?.rotation || 0;
  }

  public get nextPhoto(): string {
    return this.lastPhotos[this.nextPhotoIndex]?.url || '';
  }

  public get nextPhotoRotation(): number {
    return this.lastPhotos[this.nextPhotoIndex]?.rotation || 0;
  }

  public get isAdmin(): boolean {
    return (
      this.user !== undefined &&
      (this.project?.owner.id === this.user.id || this.project.administratorId.includes(this.user.id))
    );
  }
}
